/**
 * Egreso Firebase Repository - Repositorio real para egresos en Firestore
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  onSnapshot, 
  Timestamp,
  type DocumentData 
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { Egreso } from '../../../shared/types';
import { LoggerService } from '../../../shared/services/logger.service';

export class EgresoFirebaseRepository {
  private readonly collectionName = 'egresos';

  /**
   * Suscripción en tiempo real a los egresos del usuario
   */
  subscribeToEgresos(userId: string, callback: (egresos: Egreso[]) => void): () => void {
    console.log('🔥 EGRESO REPOSITORY - Iniciando suscripción:', { userId, collection: this.collectionName });
    LoggerService.info('Subscribiendo a egresos en tiempo real', 'EgresoFirebaseRepository', { userId });

    // Temporalmente sin orderBy para debugging
    const egresosQuery = query(
      collection(db, this.collectionName),
      where('userId', '==', userId)
      // orderBy('fecha', 'desc') - Comentado temporalmente
    );

    const unsubscribe = onSnapshot(
      egresosQuery,
      (snapshot) => {
        console.log('📊 SNAPSHOT EGRESOS:', {
          size: snapshot.size,
          empty: snapshot.empty,
          docs: snapshot.docs.length,
          userId,
          fromCache: snapshot.metadata.fromCache,
          hasPendingWrites: snapshot.metadata.hasPendingWrites
        });

        const egresos: Egreso[] = snapshot.docs.map(doc => {
          const data = doc.data() as DocumentData;
          return {
            id: doc.id,
            userId: data.userId,
            concepto: data.concepto || data.descripcion, // Compatibilidad con datos anteriores
            monto: data.monto,
            fecha: data.fecha instanceof Timestamp ? data.fecha.toDate() : new Date(data.fecha),
            categoria: data.categoria,
            descripcion: data.descripcion || data.concepto,
            esGastoFijo: data.esGastoFijo || false,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt)
          };
        });

        console.log('✅ CALLBACK EGRESOS:', { count: egresos.length, egresos });

        LoggerService.info('Egresos actualizados desde Firebase', 'EgresoFirebaseRepository', { 
          userId, 
          count: egresos.length 
        });

        callback(egresos);
      },
      (error) => {
        console.error('❌ ERROR EN SNAPSHOT EGRESOS:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          userId
        });
        LoggerService.error('Error al suscribirse a egresos', 'EgresoFirebaseRepository', { 
          userId, 
          error: error.message 
        });
        callback([]); // Retornar array vacío en caso de error
      }
    );

    return unsubscribe;
  }

  /**
   * Crear un nuevo egreso
   */
  async createEgreso(userId: string, egresoData: Omit<Egreso, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      LoggerService.info('Creando nuevo egreso', 'EgresoFirebaseRepository', { userId, egresoData });

      const now = new Date();
      const docData = {
        userId,
        concepto: egresoData.concepto,
        monto: egresoData.monto,
        fecha: Timestamp.fromDate(egresoData.fecha),
        categoria: egresoData.categoria,
        descripcion: egresoData.descripcion || egresoData.concepto,
        esGastoFijo: egresoData.esGastoFijo || false,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now)
      };

      const docRef = await addDoc(collection(db, this.collectionName), docData);

      LoggerService.logUserAction('egreso_created', 'EgresoFirebaseRepository', { 
        userId, 
        egresoId: docRef.id,
        monto: egresoData.monto,
        categoria: egresoData.categoria
      });

      return docRef.id;
    } catch (error) {
      LoggerService.error('Error al crear egreso', 'EgresoFirebaseRepository', { 
        userId, 
        error: error instanceof Error ? error.message : 'Error desconocido'
      });
      throw error;
    }
  }

  /**
   * Actualizar un egreso existente
   */
  async updateEgreso(userId: string, id: string, egresoData: Partial<Omit<Egreso, 'id' | 'userId' | 'createdAt'>>): Promise<void> {
    try {
      LoggerService.info('Actualizando egreso', 'EgresoFirebaseRepository', { userId, id, egresoData });

      const docRef = doc(db, this.collectionName, id);
      const updateData: Record<string, unknown> = {
        ...egresoData,
        updatedAt: Timestamp.fromDate(new Date())
      };

      // Convertir fecha a Timestamp si existe
      if (egresoData.fecha) {
        updateData.fecha = Timestamp.fromDate(egresoData.fecha);
      }

      // Asegurar compatibilidad entre concepto y descripción
      if (egresoData.concepto) {
        updateData.descripcion = egresoData.descripcion || egresoData.concepto;
      }

      await updateDoc(docRef, updateData);

      LoggerService.logUserAction('egreso_updated', 'EgresoFirebaseRepository', { 
        userId, 
        egresoId: id 
      });

    } catch (error) {
      LoggerService.error('Error al actualizar egreso', 'EgresoFirebaseRepository', { 
        userId, 
        id, 
        error: error instanceof Error ? error.message : 'Error desconocido'
      });
      throw error;
    }
  }

  /**
   * Eliminar un egreso
   */
  async deleteEgreso(userId: string, id: string): Promise<void> {
    try {
      LoggerService.info('Eliminando egreso', 'EgresoFirebaseRepository', { userId, id });

      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);

      LoggerService.logUserAction('egreso_deleted', 'EgresoFirebaseRepository', { 
        userId, 
        egresoId: id 
      });

    } catch (error) {
      LoggerService.error('Error al eliminar egreso', 'EgresoFirebaseRepository', { 
        userId, 
        id, 
        error: error instanceof Error ? error.message : 'Error desconocido'
      });
      throw error;
    }
  }

  /**
   * Migrar datos mock a Firebase (función de utilidad)
   */
  async migrateMockData(userId: string, mockEgresos: Omit<Egreso, 'userId'>[]): Promise<void> {
    try {
      LoggerService.info('Iniciando migración de datos mock a Firebase', 'EgresoFirebaseRepository', { 
        userId, 
        count: mockEgresos.length 
      });

      const promises = mockEgresos.map(async (egreso) => {
        const egresoData = {
          concepto: egreso.concepto,
          monto: egreso.monto,
          fecha: egreso.fecha,
          categoria: egreso.categoria,
          descripcion: egreso.descripcion
        };

        return this.createEgreso(userId, egresoData);
      });

      await Promise.all(promises);

      LoggerService.logUserAction('mock_data_migrated', 'EgresoFirebaseRepository', { 
        userId, 
        type: 'egresos',
        count: mockEgresos.length 
      });

    } catch (error) {
      LoggerService.error('Error en migración de datos mock', 'EgresoFirebaseRepository', { 
        userId, 
        error: error instanceof Error ? error.message : 'Error desconocido'
      });
      throw error;
    }
  }
}